import {
  CurrentLocationMenu,
  CurrentLocation as IconCurrentLocation,
} from '@ahamove/icons/map';
import type { Address } from '@ahm/api-wrappers-core';
import { useLocationStore } from '@ahm/react-map/store';
import { ActionIcon, Combobox, rem, Stack } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useMemo } from 'react';
import classes from '../address-autocomplete.module.css';
import { AddressAutocompleteItem } from '../autocomplete-item';
import { createTranslations, locales } from '../locales';
import type { CurrentAddressOptionsProps } from '../types';

const CurrentAddressOptions = ({
  currentInputValue,
  lang = 'vi',
  handleSelectAddress,
}: CurrentAddressOptionsProps & {
  handleSelectAddress: (address: Address) => void;
}) => {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const t = createTranslations({ locales, lang });
  const detectLocation = useLocationStore.use.detectLocation();
  const loadingLocation = useLocationStore.use.loading();
  const error = useLocationStore.use.error();
  const currentLocation = useLocationStore.use.currentLocation();

  const secondary = useMemo(() => {
    if (loadingLocation) {
      return t('getting_current_location');
    }

    if (error && 'code' in error) {
      switch (Number(error.code)) {
        case 1:
          return t('error_permission_denied');
        case 2:
          return t('error_get_current_location');
        default:
          return t('error_timeout_expired');
      }
    }

    return currentLocation?.address ?? '';
  }, [currentLocation?.address, error, loadingLocation, t]);

  if (!isMobile) return;

  return (
    <Stack gap={0} bg="white">
      <Combobox.Option
        disabled={loadingLocation}
        className={classes.option}
        title={currentLocation?.address}
        value={currentLocation?.address ?? ''}
        onClick={() => {
          if (currentLocation) {
            handleSelectAddress(currentLocation);
          }
        }}
      >
        <AddressAutocompleteItem
          hls={currentInputValue}
          primary={
            loadingLocation
              ? t('checking_location')
              : error
                ? t('current_location')
                : t('use_current_location')
          }
          primaryProp={{
            c: error ? 'red.5' : 'blue.5',
            fz: isMobile ? rem(12) : rem(14),
          }}
          secondary={secondary}
          secondaryProp={{ fz: isMobile ? rem(10) : rem(12) }}
          icon={<IconCurrentLocation width={24} height={24} />}
          rightSection={
            <ActionIcon
              size="md"
              variant="default"
              color="neutral.7"
              loading={loadingLocation}
              styles={{
                root: { borderColor: 'transparent' },
              }}
              onClick={() => {
                detectLocation()
                  .then((res) => handleSelectAddress(res))
                  .catch((error) => {
                    console.error(error);
                  });
              }}
            >
              <CurrentLocationMenu width={20} height={20} />
            </ActionIcon>
          }
        />
      </Combobox.Option>
    </Stack>
  );
};

export default CurrentAddressOptions;
